import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import Dialog from "components/DesignComponents/Dialog";
import AlertContext from "context/alert/alertContext";
import ParamsContext from "context/params/paramsContext";
import { ENDPOINTS, PLATFORMS, TABLE_NAMES, useApi } from "shared";

import { GROUP_ACTIONS } from "../../../types";
import DialogActions from "../../components/DialogActions";
import { DropdownValue } from "../../types";
import { INITIAL_EXTENSION_STATUS, INITIAL_FILES } from "./constants";
import { AddFiles, Initial } from "./content";
import {
    Files,
    MANAGE_GROUP_STEPS,
    ManageGroupProps,
    PAYLOAD_FILE_TYPES
} from "./types";

const ManageGroup = ({ action, isOpen, rowData, close }: ManageGroupProps) => {
    const { removeAlertForBGAction, setBackgroundActionId } =
        useContext(AlertContext);

    const { setReloadItems } = useContext(ParamsContext);

    const { t } = useTranslation();
    const { handleResponse, postData, updateData } = useApi();

    const [step, setStep] = useState(MANAGE_GROUP_STEPS.Initial);
    const [isLoading, setIsLoading] = useState(false);
    const [isBgActionLoading, setIsBgActionLoading] = useState(false);
    const [isSmartGroup, setIsSmartGroup] = useState(false);
    const [name, setName] = useState("");
    const [company, setCompany] = useState<DropdownValue | null>(null);
    const [files, setFiles] = useState(INITIAL_FILES);

    const [isGroupExtended, setIsGroupExtended] = useState(
        INITIAL_EXTENSION_STATUS
    );

    useEffect(() => {
        if (rowData) {
            const {
                name,
                company,
                configuration,
                firmware,
                ft_configuration,
                ft_firmware,
                ft_one_wire_auth_list_file,
                ft_bluetooth_auth_list_file
            } = rowData;

            const fileFields = [
                configuration,
                firmware,
                ft_configuration,
                ft_firmware,
                ft_one_wire_auth_list_file,
                ft_bluetooth_auth_list_file
            ];

            const isAtLeastOneFileSelected = fileFields.some(Boolean);

            setIsSmartGroup(isAtLeastOneFileSelected);
            setName(name);
            setCompany(company);

            const newFtFileValues = {
                [PAYLOAD_FILE_TYPES.FtFirmwareFileId]: ft_firmware ?? null,
                [PAYLOAD_FILE_TYPES.FtConfigurationFileId]:
                    ft_configuration ?? null,
                [PAYLOAD_FILE_TYPES.FtOneWireAuthorizationListFileId]:
                    ft_one_wire_auth_list_file ?? null,
                [PAYLOAD_FILE_TYPES.FtBluetoothAuthorizationListFileId]:
                    ft_bluetooth_auth_list_file ?? null
            };

            const newFmxFileValues = {
                [PAYLOAD_FILE_TYPES.FirmwareFileId]: firmware ?? null,
                [PAYLOAD_FILE_TYPES.ConfigurationFileId]: configuration ?? null
            };

            const newAllFileValues = {
                ...newFtFileValues,
                ...newFmxFileValues
            };

            setFiles(newAllFileValues);

            const isGroupHasSelectedFiles = (files: Partial<Files>) =>
                Object.values(files).some(file => file !== null);

            setIsGroupExtended({
                [PLATFORMS.Ft]:
                    isGroupHasSelectedFiles(newFtFileValues) ||
                    !isGroupHasSelectedFiles(newAllFileValues),
                [PLATFORMS.Fmx]: isGroupHasSelectedFiles(newFmxFileValues)
            });
        }
    }, [rowData]);

    const isCreateGroup = action === GROUP_ACTIONS.CreateGroup;
    const isAddFilesStep = step === MANAGE_GROUP_STEPS.AddFiles;

    const handleFilesChange = (
        type: PAYLOAD_FILE_TYPES,
        value: DropdownValue | null
    ) => setFiles(prev => ({ ...prev, [type]: value }));

    const toggleGroupExtensionChange = (
        platform: PLATFORMS.Ft | PLATFORMS.Fmx
    ) => setIsGroupExtended(prev => ({ ...prev, [platform]: !prev[platform] }));

    const handleCloseDialog = () => close(action);

    const closeAndReload = () => {
        handleCloseDialog();
        isCreateGroup && setReloadItems(TABLE_NAMES.DeviceGroups);
    };

    const removeLoading = () => {
        setIsLoading(false);
        setIsBgActionLoading(false);
    };

    const handleSubmit = async () => {
        removeAlertForBGAction();

        try {
            setIsLoading(true);

            const filesPayload = Object.fromEntries(
                Object.entries(files).map(([key, value]) => [
                    key,
                    value?.id ?? null
                ])
            );

            const payload = {
                name: name.trim(),
                ...(isCreateGroup ? { company_id: company?.id } : {}),
                ...filesPayload
            };

            const response = await (isCreateGroup
                ? postData(ENDPOINTS.Groups, payload)
                : updateData(ENDPOINTS.Groups, rowData?.id, payload));

            const bgActionId = response.data.create_tasks_action_id;

            const isBgActionCreated =
                bgActionId !== null && bgActionId !== undefined;

            if (isBgActionCreated) {
                const message = response.data.message;

                setIsBgActionLoading(true);

                setBackgroundActionId(
                    { message, background_action_id: bgActionId },
                    TABLE_NAMES.DeviceGroups,
                    () => closeAndReload(),
                    () => removeLoading()
                );
            } else {
                closeAndReload();
                handleResponse(response);
            }
        } catch (error) {
            handleResponse(error);
            removeLoading();
        }
    };

    const isReady = () => {
        const hasName = Boolean(name.trim());
        const hasCompany = company !== null;
        const hasFiles = Object.values(files).some(file => file !== null);

        if (!hasName) return false;
        if (isCreateGroup && !hasCompany) return false;
        if (isAddFilesStep && !hasFiles) return false;
        return true;
    };

    const getConfirmationBtnTitle = () => {
        if (isAddFilesStep) {
            return isCreateGroup ? t("Button##create") : t("Button##save");
        }

        if (isSmartGroup) {
            return t("Button##next");
        }

        return isCreateGroup ? t("Button##create") : t("Button##save");
    };

    const steps = {
        [MANAGE_GROUP_STEPS.Initial]: {
            title: isCreateGroup
                ? t("Dialog##create group")
                : t("General##edit group"),
            description: isCreateGroup
                ? t("Dialog##create group description")
                : t("Dialog##edit group description"),
            content: (
                <Initial
                    isLoading={isLoading}
                    isChecked={isSmartGroup}
                    name={name}
                    company={isCreateGroup ? company : undefined}
                    onIsSmartGroupChange={value => {
                        setIsSmartGroup(value);

                        if (!value) {
                            setFiles(INITIAL_FILES);
                            setIsGroupExtended(INITIAL_EXTENSION_STATUS);
                        }
                    }}
                    onChangeName={setName}
                    onCompanyChange={setCompany}
                />
            ),
            onSecondaryClick: handleCloseDialog,
            onMainClick: () =>
                isSmartGroup
                    ? setStep(MANAGE_GROUP_STEPS.AddFiles)
                    : handleSubmit()
        },
        [MANAGE_GROUP_STEPS.AddFiles]: {
            title: isCreateGroup
                ? t("Dialog##create smart group")
                : t("General##edit smart group"),
            description: isCreateGroup
                ? t("Dialog##create smart group description")
                : t("Dialog##edit smart group description"),
            content: (
                <AddFiles
                    fileValues={files}
                    company={company!}
                    isLoading={isLoading}
                    isGroupExtended={isGroupExtended}
                    onFileChange={handleFilesChange}
                    onGroupExtensionChange={toggleGroupExtensionChange}
                />
            ),
            onSecondaryClick: () => setStep(MANAGE_GROUP_STEPS.Initial),
            onMainClick: handleSubmit
        }
    };

    const title = steps[step].title;
    const description = steps[step].description;
    const content = steps[step].content;
    const onSecondaryClick = steps[step].onSecondaryClick;
    const onMainClick = steps[step].onMainClick;

    return (
        <Dialog
            title={title}
            description={description}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={isBgActionLoading ? undefined : handleCloseDialog}
            submit={onMainClick}
            slotProps={{
                transition: {
                    onExited: () => {
                        removeAlertForBGAction();
                        removeLoading();
                        setStep(MANAGE_GROUP_STEPS.Initial);
                        setIsSmartGroup(false);
                        setName("");
                        setCompany(null);
                        setFiles(INITIAL_FILES);
                        setIsGroupExtended(INITIAL_EXTENSION_STATUS);
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={onSecondaryClick}
                    isLoading={isLoading || isBgActionLoading}
                    isReadyToConfirm={isReady()}
                    secondaryButtonText={
                        isAddFilesStep ? t("Button##back") : t("Button##close")
                    }
                    confirmationButtonText={getConfirmationBtnTitle()}
                    cancelButtonTestId="cancel-manage-group"
                    confirmationButtonTestId="confirm-manage-group"
                />
            }
            extendToMaxWidth
        >
            {content}
        </Dialog>
    );
};

export default ManageGroup;
